import React, { useEffect, useState } from 'react';
import { waitlistAPI } from '../api';
import { useAuth } from '../AuthContext';

const ADMIN_EMAIL = 'yjc801@gmail.com';

function AdminWaitlist() {
  const { user } = useAuth();
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [invitingId, setInvitingId] = useState(null);

  useEffect(() => {
    loadWaitlist();
  }, []);

  const loadWaitlist = async () => {
    try {
      setLoading(true);
      const response = await waitlistAPI.getWaitlist();
      setWaitlist(response.data.waitlist);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (waitlistId) => {
    setInvitingId(waitlistId);
    setError('');
    setMessage('');

    try {
      await waitlistAPI.inviteFromWaitlist(waitlistId);
      setMessage('Registration invite sent successfully.');
      await loadWaitlist();
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInvitingId(null);
    }
  };

  const renderAdminGuard = () => (
    <div className="container">
      <div className="card">
        <h2>Admin Access Required</h2>
        <p>This page is only available to administrators.</p>
      </div>
    </div>
  );

  if (!user || user.email.toLowerCase() !== ADMIN_EMAIL) {
    return renderAdminGuard();
  }

  const renderStatusBadge = (status) => {
    const normalized = status || 'waiting';
    const config = {
      waiting: { label: 'Waiting', background: '#f0ad4e' },
      invited: { label: 'Email Sent', background: '#17a2b8' }
    };
    const { label, background } = config[normalized] || config.waiting;
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '0.15rem 0.6rem',
          borderRadius: '999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#fff',
          background
        }}
      >
        {label}
      </span>
    );
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Waitlist Management</h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Review waitlist entries and send them registration invites when space becomes available.
        </p>

        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={loadWaitlist} disabled={loading}>
            Refresh
          </button>
        </div>

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading waitlist...</p>
          </div>
        ) : waitlist.length === 0 ? (
          <div className="info-message">
            No entries on the waitlist right now.
          </div>
        ) : (
          <div style={{ marginTop: '1rem' }}>
            {waitlist.map((entry) => (
              <div
                key={entry.id}
                className="member-item"
                style={{ alignItems: 'flex-start', gap: '1rem' }}
              >
                <div style={{ flex: 1 }}>
                  <strong>{entry.name}</strong>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>{entry.email}</div>
                  <div style={{ marginTop: '0.35rem' }}>
                    {renderStatusBadge(entry.status)}
                    {entry.invited_at && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#777' }}>
                        Sent: {new Date(entry.invited_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {entry.message && (
                    <div style={{ marginTop: '0.5rem', fontStyle: 'italic', color: '#555' }}>
                      "{entry.message}"
                    </div>
                  )}
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#888' }}>
                    Added: {new Date(entry.created_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ minWidth: '200px', alignSelf: 'center' }}>
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => handleInvite(entry.id)}
                    disabled={invitingId === entry.id || entry.status === 'invited'}
                  >
                    {entry.status === 'invited'
                      ? 'Email Sent'
                      : invitingId === entry.id
                        ? 'Sending...'
                        : 'Send Invite'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminWaitlist;
